// src/components/dashboard/Dashboard.tsx
import React, { useState } from 'react';
import '../styles/Dashboard.css';

// Icons as React components
const FiHome = () => <span>🏠</span>;
const FiPlus = () => <span>➕</span>;
const FiUsers = () => <span>👥</span>;
const FiSettings = () => <span>⚙️</span>;
const FiBarChart2 = () => <span>📊</span>;
const FiLogOut = () => <span>🚪</span>;
const FaDog = () => <span>🐶</span>;
const FaCat = () => <span>🐱</span>;
const FaPaw = () => <span>🐾</span>;

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [pets, setPets] = useState([
    { id: 1, name: 'Sunny', type: 'Golden Retriever', age: '12 weeks', status: 'Available', views: 142 },
    { id: 2, name: 'Luna', type: 'Labrador', age: '10 weeks', status: 'Pending', views: 98 },
    { id: 3, name: 'Max', type: 'French Bulldog', age: '8 weeks', status: 'Adopted', views: 210 },
    { id: 4, name: 'Bella', type: 'Beagle', age: '11 weeks', status: 'Available', views: 76 },
  ]);
  
  const [newPet, setNewPet] = useState({
    name: '',
    type: '',
    breed: '',
    age: '',
    price: '',
    description: ''
  });
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewPet({ ...newPet, [name]: value });
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newPetData = {
      id: pets.length + 1,
      name: newPet.name,
      type: newPet.type,
      age: newPet.age,
      status: 'Available',
      views: 0
    };
    setPets([...pets, newPetData]);
    setNewPet({ name: '', type: '', breed: '', age: '', price: '', description: '' });
  };
  
  const stats = [
    { title: 'Total Pets', value: '42', icon: <FaPaw />, change: '+12%' },
    { title: 'Adoptions', value: '28', icon: <FiUsers />, change: '+8%' },
    { title: 'Pending', value: '7', icon: <FiBarChart2 />, change: '-3%' },
    { title: 'New This Week', value: '5', icon: <FiPlus />, change: '+15%' }
  ];

  return (
    <div className="dashboard">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="logo">
          <FaPaw />
          <h2>PawDashboard</h2>
        </div>
        
        <ul className="nav-links">
          <li className={activeTab === 'dashboard' ? 'active' : ''} onClick={() => setActiveTab('dashboard')}>
            <FiHome />
            <span>Dashboard</span>
          </li>
          <li className={activeTab === 'pets' ? 'active' : ''} onClick={() => setActiveTab('pets')}>
            <FaDog />
            <span>Manage Pets</span>
          </li>
          <li className={activeTab === 'add' ? 'active' : ''} onClick={() => setActiveTab('add')}>
            <FiPlus />
            <span>Add New Pet</span>
          </li>
          <li className={activeTab === 'users' ? 'active' : ''} onClick={() => setActiveTab('users')}>
            <FiUsers />
            <span>Users</span>
          </li>
          <li className={activeTab === 'settings' ? 'active' : ''} onClick={() => setActiveTab('settings')}>
            <FiSettings />
            <span>Settings</span>
          </li>
        </ul>
        
        <div className="logout">
          <FiLogOut />
          <span>Logout</span>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="main-content">
        {/* Header */}
        <div className="header">
          <div className="search-bar">
            <input type="text" placeholder="Search..." />
          </div>
          <div className="user-info">
            <div className="user-avatar">
              <img src="https://randomuser.me/api/portraits/women/44.jpg" alt="User" />
            </div>
            <div className="user-details">
              <span className="user-name">Sarah Johnson</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
        
        {/* Dashboard Content */}
        <div className="content">
          <div className="page-title">
            <h1>Pet Management Dashboard</h1>
            <p>Manage and track all your pets in one place</p>
          </div>
          
          {/* Stats Overview */}
          {activeTab === 'dashboard' && (
            <>
              <div className="stats-grid">
                {stats.map((stat, index) => (
                  <div className="stat-card" key={index}>
                    <div className="stat-icon">{stat.icon}</div>
                    <div className="stat-info">
                      <h3>{stat.value}</h3>
                      <p>{stat.title}</p>
                    </div>
                    <div className="stat-change">{stat.change}</div>
                  </div>
                ))}
              </div>
              
              <div className="charts-container">
                <div className="chart-card">
                  <h3>Adoption Rate</h3>
                  <div className="chart-placeholder">
                    <div className="chart-bar" style={{ height: '70%' }}></div>
                    <div className="chart-bar" style={{ height: '85%' }}></div>
                    <div className="chart-bar" style={{ height: '60%' }}></div>
                    <div className="chart-bar" style={{ height: '90%' }}></div>
                    <div className="chart-bar" style={{ height: '75%' }}></div>
                  </div>
                </div>
                
                <div className="chart-card">
                  <h3>Pet Types Distribution</h3>
                  <div className="pie-chart">
                    <div className="pie-segment dogs"></div>
                    <div className="pie-segment cats"></div>
                    <div className="pie-segment others"></div>
                    <div className="pie-center"></div>
                  </div>
                  <div className="chart-legend">
                    <div><span className="legend-dot dogs"></span> Dogs (65%)</div>
                    <div><span className="legend-dot cats"></span> Cats (25%)</div>
                    <div><span className="legend-dot others"></span> Others (10%)</div>
                  </div>
                </div>
              </div>
            </>
          )}
          
          {/* Manage Pets */}
          {activeTab === 'pets' && (
            <div className="pets-table-container">
              <div className="table-header">
                <h2>Manage Pets</h2>
                <button className="btn-primary" onClick={() => setActiveTab('add')}>
                  <FiPlus /> Add New Pet
                </button>
              </div>
              
              <div className="table-responsive">
                <table className="pets-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Age</th>
                      <th>Status</th>
                      <th>Views</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pets.map(pet => (
                      <tr key={pet.id}>
                        <td>
                          <div className="pet-info">
                            <div className="pet-avatar">
                              {pet.type.includes('Retriever') || pet.type.includes('Labrador') ? <FaDog /> : <FaCat />}
                            </div>
                            {pet.name}
                          </div>
                        </td>
                        <td>{pet.type}</td>
                        <td>{pet.age}</td>
                        <td>
                          <span className={`status-badge ${pet.status.toLowerCase()}`}>
                            {pet.status}
                          </span>
                        </td>
                        <td>{pet.views}</td>
                        <td>
                          <div className="actions">
                            <button className="btn-edit">Edit</button>
                            <button className="btn-delete">Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Add New Pet */}
          {activeTab === 'add' && (
            <div className="add-pet-form">
              <div className="form-header">
                <h2>Add New Pet</h2>
                <p>Fill in the details to add a new pet to your inventory</p>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="name">Pet Name</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      value={newPet.name}
                      onChange={handleInputChange}
                      placeholder="Enter pet name"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="type">Animal Type</label>
                    <input 
                      type="text" 
                      id="type" 
                      name="type" 
                      value={newPet.type}
                      onChange={handleInputChange}
                      placeholder="Dog, Cat, etc."
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="breed">Breed</label>
                    <input 
                      type="text" 
                      id="breed" 
                      name="breed" 
                      value={newPet.breed}
                      onChange={handleInputChange}
                      placeholder="Enter breed"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="age">Age</label>
                    <input 
                      type="text" 
                      id="age" 
                      name="age" 
                      value={newPet.age}
                      onChange={handleInputChange}
                      placeholder="e.g. 12 weeks"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="price">Price ($)</label>
                    <input 
                      type="text" 
                      id="price" 
                      name="price" 
                      value={newPet.price}
                      onChange={handleInputChange}
                      placeholder="Enter price"
                      required
                    />
                  </div>
                  
                  <div className="form-group full-width">
                    <label htmlFor="description">Description</label>
                    <textarea 
                      id="description" 
                      name="description" 
                      value={newPet.description}
                      onChange={handleInputChange}
                      placeholder="Describe the pet's characteristics, personality, etc."
                      rows={4}
                      required
                    ></textarea>
                  </div>
                  
                  <div className="form-group full-width">
                    <label>Upload Photos</label>
                    <div className="upload-area">
                      <div className="upload-icon">
                        <FiPlus />
                      </div>
                      <p>Drag & drop images here or click to browse</p>
                      <span>Supports JPG, PNG - Max 5MB per image</span>
                    </div>
                  </div>
                </div>
                
                <div className="form-actions">
                  <button type="button" className="btn-secondary">Cancel</button>
                  <button type="submit" className="btn-primary">Add Pet</button>
                </div>
              </form>
            </div>
          )}
          
          {/* Settings */}
          {activeTab === 'settings' && (
            <div className="settings-container">
              <h2>Account Settings</h2>
              <div className="settings-card">
                <div className="settings-group">
                  <h3>Profile Information</h3>
                  <div className="settings-item">
                    <label>Full Name</label>
                    <input type="text" value="Sarah Johnson" />
                  </div>
                  <div className="settings-item">
                    <label>Email Address</label>
                    <input type="email" value="sarah@vivapets.com" />
                  </div>
                  <div className="settings-item">
                    <label>Phone Number</label>
                    <input type="tel" value="(555) 123-4567" />
                  </div>
                </div>
                
                <div className="settings-group">
                  <h3>Password</h3>
                  <div className="settings-item">
                    <label>Current Password</label>
                    <input type="password" placeholder="Enter current password" />
                  </div>
                  <div className="settings-item">
                    <label>New Password</label>
                    <input type="password" placeholder="Enter new password" />
                  </div>
                  <div className="settings-item">
                    <label>Confirm Password</label>
                    <input type="password" placeholder="Confirm new password" />
                  </div>
                </div>
                
                <div className="settings-actions">
                  <button className="btn-secondary">Discard Changes</button>
                  <button className="btn-primary">Save Settings</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;